import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { type Currency, currencySchema, formatMoney } from '@kon/shared';

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  /** Format base-currency cents in the selected display currency. */
  format: (baseCents: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);
const STORAGE_KEY = 'kon.currency';

function readStored(): Currency {
  if (typeof window === 'undefined') return 'USD';
  const parsed = currencySchema.safeParse(window.localStorage.getItem(STORAGE_KEY));
  return parsed.success ? parsed.data : 'USD';
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(readStored);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      setCurrency,
      format: (baseCents: number) => formatMoney(baseCents, currency),
    }),
    [currency, setCurrency],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
